import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";

import { setSection } from "../../../assignmentSlice";

import { typeInfo } from "../../../../../constants/typeInfo";
import {
  normalizeEnglishAnswer,
  parseParagraphSummaryBlank,
} from "../../../../../utils/textUtils";

import UserInputBar from "../../widgets/UserInputBar";
import CommonPage from "../../common/CommonPage";

const SummaryBlankPage = ({
  section,
  pageList,
  currentPage,
  currentIndex,
  setPageList,
  handlePreviousPage,
  handleNextPage,
}) => {
  const dispatch = useDispatch();
  const [userInput, setUserInput] = useState("");
  const [overlayVisible, setOverlayVisible] = useState(false);

  const parsedSummary = useMemo(
    () => parseParagraphSummaryBlank(currentPage.text2),
    [currentPage.text2]
  );

  useEffect(() => {
    if (currentPage.isSubmitted) {
      const answer = String(currentPage.answer || "");
      const hint = parsedSummary.hint;
      setUserInput(
        answer.toLowerCase().startsWith(hint.toLowerCase())
          ? answer.slice(hint.length)
          : answer
      );
    } else {
      setUserInput(currentPage.input || "");
    }
  }, [
    currentIndex,
    currentPage.answer,
    currentPage.input,
    currentPage.isSubmitted,
    parsedSummary.hint,
  ]);

  const handleChange = (event) => {
    setUserInput(event.target.value);
  };

  const handleSubmit = () => {
    if (currentPage.isSubmitted) return;
    if (userInput.trim() === "") return alert("빈칸을 채워주세요.");

    const submittedAnswer = `${parsedSummary.hint}${userInput}`;
    const correct =
      normalizeEnglishAnswer(submittedAnswer) ===
      normalizeEnglishAnswer(currentPage.answer);

    const newPageList = pageList.map((page, index) =>
      index === currentIndex
        ? {
            ...page,
            input: userInput,
            isSubmitted: true,
            isCorrect: correct,
          }
        : page
    );

    setOverlayVisible(true);
    setPageList(newPageList);
    dispatch(setSection({ ...section, answer: newPageList }));

    setTimeout(() => {
      setOverlayVisible(false);
      const answer = String(currentPage.answer || "");
      setUserInput(
        answer.toLowerCase().startsWith(parsedSummary.hint.toLowerCase())
          ? answer.slice(parsedSummary.hint.length)
          : answer
      );
    }, 600);
  };

  const displayedBlank = userInput || "________";

  const isMobileW = Math.min(window.innerWidth, window.screen.width) <= 768; // [모의고사 리디자인]

  return (
    <CommonPage
      currentIndex={currentIndex}
      handleNextPage={handleNextPage}
      handlePreviousPage={handlePreviousPage}
      totalNo={pageList.length}
      currentPage={currentPage}
      paragraph={currentPage.text || currentPage.paragraph}
      accentColor={typeInfo.paragraph.color}
      overlayVisible={overlayVisible}
      isCorrect={currentPage.isCorrect}
      handleSubmit={handleSubmit}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: isMobileW ? "14px" : "20px",
          // [여백 수술] 폰은 화면 기준 폭 — 조상 요소가 좁혀도 면역
          width: isMobileW ? `${Math.min(window.innerWidth, window.screen.width) - 56}px` : "100%",
          maxWidth: "680px", // [모의고사 리디자인] 중앙 열
          margin: "0 auto",
        }}
      >
        {/* [모의고사 리디자인] Q칩 + 카운터 + 진행바 */}
        <div style={{ width: "100%" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
            <span style={{ backgroundColor: "#0E141B", color: "#FFF", borderRadius: "8px", fontSize: isMobileW ? "12px" : "13px", fontWeight: 800, padding: "2px 10px" }}>
              Q{currentIndex + 1}
            </span>
            <span style={{ marginLeft: "auto", fontSize: isMobileW ? "12px" : "13px", color: "#7A8490", fontWeight: 700 }}>
              {currentIndex + 1} / {pageList.length}
            </span>
          </div>
          <div style={{ height: "4px", borderRadius: "999px", backgroundColor: "#EDF2F1", overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${((currentIndex + 1) / Math.max(pageList.length, 1)) * 100}%`, backgroundColor: typeInfo.paragraph.color, borderRadius: "999px", transition: "width .25s" }} />
          </div>
        </div>
        <div style={{ width: "100%", fontSize: isMobileW ? "16px" : "19px", fontWeight: 600, lineHeight: 1.7, textAlign: "left", wordBreak: "keep-all" }}>
          {currentPage.question}
        </div>

        <div
          style={{
            width: "100%",
            boxSizing: "border-box",
            padding: isMobileW ? "13px 14px" : "16px 20px",
            border: "1px solid #E6EDEC", // [모의고사 리디자인] 카드 스킨
            borderRadius: "12px",
            backgroundColor: "#FFFFFF",
            fontSize: isMobileW ? "16px" : "19px",
            lineHeight: "1.9",
            whiteSpace: "pre-wrap",
            wordBreak: "keep-all",
          }}
        >
          <div
            style={{
              marginBottom: "8px",
              color: typeInfo.paragraph.color,
              fontWeight: 800,
              fontSize: isMobileW ? "12px" : "13px",
              letterSpacing: "0.06em",
            }}
          >
            {(currentPage.text2_heading || "<요약문>").replace(/[<>]/g, "")}
          </div>
          {parsedSummary.prefix}
          <span
            style={{
              color: typeInfo.paragraph.color,
              fontWeight: "bold",
              borderBottom: "1px solid #737373",
              padding: "0 4px",
            }}
          >
            {parsedSummary.hint}
            {displayedBlank}
          </span>
          {parsedSummary.suffix}
        </div>

        <div style={{ position: "relative", width: isMobileW ? "100%" : "70%", marginTop: "8px", marginBottom: isMobileW ? "48px" : 0 }}>
          <UserInputBar
            handleSetAnswer={handleSubmit}
            handleChange={handleChange}
            userInput={userInput}
            type="paragraph"
            onlyUnderline={true}
            isSubmitted={currentPage.isSubmitted}
            hint={parsedSummary.hint}
          />
        </div>
      </div>
    </CommonPage>
  );
};

export default SummaryBlankPage;
